import React, { useMemo } from "react";
import Animated from "react-native-reanimated";
import { Button } from "@/components/ui/Button";
import { DateTimePicker } from "@/components/Pickers/DatetimePicker";
import { ChoiceRendererProps, ThemedInputType, Renderer } from "./props";
import useDateTime from "@/hooks/useDateTime";
import { parse, isValid } from "date-fns";

/**
 * React components for date/datetime renderers
 */
const DateRenderer = <T,>({ cfg }: { cfg: ChoiceRendererProps<T> }) => {
  const { formatDate, settings } = useDateTime();

  // Parse current value to Date for the picker
  const initialDate = useMemo(() => {
    if (!cfg.value) return undefined;
    const parsed = parse(cfg.value, settings.dateFormat, new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [cfg.value, settings.dateFormat]);

  return (
    <DateTimePicker
      date={initialDate}
      hideTime={true}
      fireOnDayPick={false}
      onPick={(e) => {
        // Store the formatted display value - consistent with Table approach
        const formattedDate = formatDate(e.date);
        cfg.onPick?.(formattedDate as T);
      }}
    />
  );
};

const DateTimeRenderer = <T,>({ cfg }: { cfg: ChoiceRendererProps<T> }) => {
  const { formatTimestamp, settings } = useDateTime();

  // Parse current value to Date for the picker (preserves time)
  const initialDate = useMemo(() => {
    if (!cfg.value) return undefined;
    const pattern = `${settings.dateFormat} ${settings.timeFormat === "24" ? "HH:mm" : "h:mm a"}`;
    const parsed = parse(cfg.value, pattern, new Date());
    return isValid(parsed) ? parsed : undefined;
  }, [cfg.value, settings.dateFormat, settings.timeFormat]);

  return (
    <DateTimePicker
      date={initialDate}
      hideTime={false}
      fireOnDayPick={false}
      onPick={(e) => {
        // Store the formatted display value - consistent with Table approach
        const formattedDateTime = formatTimestamp(e.date, true);
        cfg.onPick?.(formattedDateTime as T);
      }}
    />
  );
};

/**
 * Default choice renderers for different input types
 */
export const DefaultRegistry: Partial<Record<ThemedInputType | "text", Renderer<any>>> = {
  date: <T,>(cfg: ChoiceRendererProps<T>) => {
    return <DateRenderer cfg={cfg} />;
  },

  "date-time": <T,>(cfg: ChoiceRendererProps<T>) => {
    return <DateTimeRenderer cfg={cfg} />;
  },

  state: <T,>(cfg: ChoiceRendererProps<T>) => {
    const items = cfg.items ?? [];
    const getKey = cfg.getKey ?? ((_: T, i: number) => String(i));
    const getLabel = cfg.getLabel ?? ((x: any) => (Array.isArray(x) && x.length >= 2 ? String(x[1]) : String(x))); // support [code,name]
    return (
      <Animated.ScrollView style={{ maxHeight: 420 }}>
        {items.map((it, i) => (
          <Button
            style={{ backgroundColor: "transparent" }}
            key={getKey(it, i)}
            text={getLabel(it) ?? `${it}`}
            onPress={() => {
              cfg.onPick?.(it);
            }}
          />
        ))}
      </Animated.ScrollView>
    );
  },

  text: <T,>(cfg: ChoiceRendererProps<T>) => {
    const items = cfg.items ?? [];
    const getKey = cfg.getKey ?? ((_: T, i: number) => String(i));
    const getLabel =
      cfg.getLabel ??
      ((x: any) => {
        if (typeof x === "object" && x !== null && "text" in x) {
          return x.text;
        }
        return String(x);
      });
    const getBgColor = (item: any) => {
      if (typeof item === "object" && item !== null && "bgColor" in item) {
        return item.bgColor || "transparent";
      }
      return "transparent";
    };

    return (
      <Animated.ScrollView style={{ maxHeight: 420 }}>
        {items.map((it, i) => (
          <Button
            style={{
              backgroundColor: getBgColor(it),
              borderColor: "transparent",
              marginVertical: 2,
            }}
            key={getKey(it, i)}
            text={getLabel(it)}
            onPress={() => {
              cfg.onPick?.(it);
            }}
          />
        ))}
      </Animated.ScrollView>
    );
  },
};

/**
 * Renders choice components based on type
 */
export function choiceRenderer<T>(cfg: ChoiceRendererProps<T>): React.ReactNode {
  const key = (cfg.type?.startsWith?.("cities-") ? "text" : cfg.type) ?? "text";
  const render = DefaultRegistry[key as keyof typeof DefaultRegistry] ?? DefaultRegistry.text!;
  return render(cfg);
}
